import React from "react";
import { Outlet, useParams, useLocation, Link } from "react-router";
import { OrgProvider } from "@/contexts/OrgContext";
import type { OrgContextValue } from "@/contexts/OrgContext";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";

import { useUserOrg } from '@/hooks/useUserOrg';

export function OrgLayout() {
  const { orgId } = useParams();
  const { data: orgContext, isLoading, isError, error } = useUserOrg(orgId);

  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Convert path segments to readable titles
  const getReadableTitle = (segment: string) => {
    if (segment === 'org') return 'Organisation';
    if (segment === orgId) return orgContext?.organisation?.name || 'Org';
    return segment.charAt(0).toUpperCase() + segment.slice(1);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="text-muted-foreground">Loading organisation…</span>
      </div>
    );
  }

  if (isError || !orgContext) {
    return (
      <div className="flex justify-center items-center h-64 text-red-600">
        {error instanceof Error ? error.message : 'Failed to load organisation'}
      </div>
    );
  }

  return (
    <OrgProvider value={orgContext}>
      <div className="min-h-screen flex flex-col">
        <div className="flex items-center gap-2 px-4">
          <Breadcrumb className="hidden md:block">
            <BreadcrumbList>
              {/* Only show Home if we're on /app exactly */}
              {location.pathname === '/app' ? (
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/app">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              ) : null}
              {pathSegments
                .filter(x => x !== 'app')
                .map((segment, index) => {
                  const isOrgId = segment === orgId;
                  const path = `/${pathSegments.slice(0, index + 1).join('/')}`;
                  const isLast = index === pathSegments.length - 1;
                  return (
                    <React.Fragment key={path}>
                      {/* Only show separator if not the first item */}
                      {index > 0 || location.pathname !== '/app' ? <BreadcrumbSeparator /> : null}
                      <BreadcrumbItem>
                        {isLast ? (
                          <BreadcrumbPage>{isOrgId ? orgContext.organisation?.name || segment : getReadableTitle(segment)}</BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink asChild>
                            <Link to={path}>{isOrgId ? orgContext.organisation?.name || segment : getReadableTitle(segment)}</Link>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  );
                })}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </OrgProvider >
  );
}

